import type {ReactNode} from "react";
import type React from "react";
import {memo, useState} from "react";

import {ChevronDownIcon} from "../../react/components/Icons/ChevronDownIcon";
import {ChevronRightIcon} from "../../react/components/Icons/ChevronRightIcon";
import {Accordion, AccordionContent, AccordionItem, AccordionTrigger} from "../ui/accordion";
import {Box} from "../ui/box";
import {HStack} from "../ui/hstack";
import {Text} from "../ui/text";
import {VStack} from "../ui/vstack";

type Props = {
    leftHeaderElement?: ReactNode;
    rightHeaderElement?: ReactNode;
} & StrictUnion<
    | {
          title: React.ReactNode | ((isOpen: boolean) => React.ReactNode);
          defaultOpen?: boolean;
          children: React.ReactNode;
      }
    | {
          disabled?: true;
          title: React.ReactNode;
          children?: React.ReactNode;
      }
>;

const Collapse: React.FC<Props> = ({
    title,
    children,
    defaultOpen,
    disabled,
    leftHeaderElement,
    rightHeaderElement
}) => {
    const [value, setValue] = useState<string[]>(disabled || defaultOpen ? ["item"] : []);

    return (
        <Accordion
            isDisabled={disabled}
            testID="collapse"
            type="single"
            value={value}
            onValueChange={setValue}
        >
            <AccordionItem value="item">
                {!disabled ? (
                    <Box className="relative">
                        <AccordionTrigger>
                            {({isExpanded}) => {
                                return (
                                    <HStack className="w-full" space="sm">
                                        <HStack
                                            className={`flex-1 ${
                                                leftHeaderElement
                                                    ? "items-baseline"
                                                    : "items-center"
                                            }`}
                                            space="sm"
                                        >
                                            {!disabled &&
                                                (isExpanded ? (
                                                    <ChevronDownIcon
                                                        color="blueGray.400"
                                                        size="sm"
                                                    />
                                                ) : (
                                                    <ChevronRightIcon
                                                        color="blueGray.400"
                                                        size="sm"
                                                    />
                                                ))}
                                            <VStack className="flex-1" space="xl">
                                                {!!leftHeaderElement && leftHeaderElement}
                                                <VStack>
                                                    {typeof title === "string" && (
                                                        <Text variant="md">{title}</Text>
                                                    )}
                                                    {typeof title === "function" &&
                                                        title(isExpanded)}
                                                    {typeof title !== "string" &&
                                                        typeof title !== "function" &&
                                                        title}
                                                </VStack>
                                            </VStack>
                                        </HStack>
                                    </HStack>
                                );
                            }}
                        </AccordionTrigger>
                        {rightHeaderElement && (
                            <Box className="h-full absolute right-0 top-0 px-5 pt-4">
                                {rightHeaderElement}
                            </Box>
                        )}
                    </Box>
                ) : (
                    <HStack
                        className="w-full justify-between grow-0 shrink-0 items-start p-5"
                        space="sm"
                    >
                        <HStack
                            className={`flex-1 ${
                                leftHeaderElement ? "items-baseline" : "items-center"
                            }`}
                            space="sm"
                        >
                            <VStack className="flex-1" space="xl">
                                {!!leftHeaderElement && leftHeaderElement}
                                <VStack>
                                    {typeof title === "string" ? (
                                        <Text variant="md">{title}</Text>
                                    ) : (
                                        title
                                    )}
                                </VStack>
                            </VStack>
                        </HStack>
                        {!!rightHeaderElement && rightHeaderElement}
                    </HStack>
                )}
                <AccordionContent>{children}</AccordionContent>
            </AccordionItem>
        </Accordion>
    );
};

export default memo(Collapse);
